import { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button, IconButton, Badge, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Stat, StatGroup, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Plus, Pencil, Trash2, StopCircle, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { useServiceDowntimeLogs, useServiceUptimeStats, type SlaDowntimeLog } from '@/api/services';
import { useDeleteDowntimeLog } from '@/api/services-mutations';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import {
  DOWNTIME_TYPE_LABELS,
  DOWNTIME_TYPE_TONE,
  DOWNTIME_CAUSE_LABELS,
  DOWNTIME_CAUSE_TONE,
  formatMinutes,
} from './sla-constants';
import { SlaDowntimeDialog } from './sla-downtime-dialog';
import { SlaEndDowntimeDialog } from './sla-end-downtime-dialog';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const PERIODS = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'this_year', label: 'This year' },
];

const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function periodRange(period: string): { start: string; end: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (period) {
    case 'last_month': return { start: fmt(new Date(y, m - 1, 1)), end: fmt(new Date(y, m, 0)) };
    case 'last_3_months': return { start: fmt(new Date(y, m - 3, 1)), end: fmt(new Date(y, m + 1, 0)) };
    case 'this_year': return { start: fmt(new Date(y, 0, 1)), end: fmt(new Date(y, 11, 31)) };
    default: return { start: fmt(new Date(y, m, 1)), end: fmt(new Date(y, m + 1, 0)) };
  }
}


/** Uptime stats card (period-scoped) + the downtime incident log with CRUD. */
export function SlaDowntimeSection({ id }: { id: number }) {
  const toast = useToast();
  const [period, setPeriod] = useState('this_month');
  const { start, end } = useMemo(() => periodRange(period), [period]);
  const stats = useServiceUptimeStats(id, start, end);
  const logs = useServiceDowntimeLogs(id);
  const del = useDeleteDowntimeLog(id);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SlaDowntimeLog | null>(null);
  const [ending, setEnding] = useState<SlaDowntimeLog | null>(null);
  const [toDelete, setToDelete] = useState<SlaDowntimeLog | null>(null);

  const rows = logs.data?.results ?? [];
  const st = stats.data;

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (log: SlaDowntimeLog) => { setEditing(log); setFormOpen(true); };
  const confirmDelete = () => {
    if (!toDelete) return;
    del.mutate(toDelete.downtime_log_id, {
      onSuccess: () => { toast.success('Downtime log deleted'); setToDelete(null); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to delete log'),
    });
  };

  const columns: ColumnDef<SlaDowntimeLog, unknown>[] = [
    { id: 'type', header: 'Type', cell: ({ row }) => <Badge tone={DOWNTIME_TYPE_TONE[row.original.downtime_type] ?? 'neutral'} appearance="subtle" size="sm">{DOWNTIME_TYPE_LABELS[row.original.downtime_type] ?? row.original.downtime_type}</Badge> },
    { id: 'cause', header: 'Cause', cell: ({ row }) => row.original.downtime_cause ? <Badge tone={DOWNTIME_CAUSE_TONE[row.original.downtime_cause] ?? 'neutral'} appearance="subtle" size="sm">{DOWNTIME_CAUSE_LABELS[row.original.downtime_cause]}</Badge> : <span style={muted}>—</span> },
    { id: 'start', header: 'Start', cell: ({ row }) => formatDateTime(row.original.downtime_start) },
    { id: 'end', header: 'End', cell: ({ row }) => row.original.downtime_end ? formatDateTime(row.original.downtime_end) : <Badge tone="danger" appearance="subtle" size="sm">Ongoing</Badge> },
    { id: 'duration', header: 'Duration', cell: ({ row }) => row.original.downtime_end ? formatMinutes(row.original.downtime_minutes) : <span style={{ color: 'var(--vn-danger-text)' }}>Ongoing</span> },
    { id: 'counts', header: () => <span style={{ display: 'block', textAlign: 'center' }}>Counts SLA</span>, cell: ({ row }) => <span style={{ display: 'flex', justifyContent: 'center' }}>{row.original.counts_against_sla ? <XCircle size={16} style={{ color: 'var(--vn-danger-text)' }} /> : <CheckCircle2 size={16} style={{ color: 'var(--vn-success-text)' }} />}</span> },
    { id: 'description', header: 'Description', cell: ({ row }) => row.original.downtime_description || <span style={muted}>—</span> },
    {
      id: 'actions',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Actions</span>,
      cell: ({ row }) => (
        <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }}>
          {!row.original.downtime_end && (
            <IconButton aria-label="End downtime" variant="ghost" size="sm" onClick={() => setEnding(row.original)}><StopCircle size={15} /></IconButton>
          )}
          <IconButton aria-label="Edit" variant="ghost" size="sm" onClick={() => openEdit(row.original)}><Pencil size={15} /></IconButton>
          <IconButton aria-label="Delete" variant="ghost" size="sm" onClick={() => setToDelete(row.original)}><Trash2 size={15} /></IconButton>
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardHeader
          actions={
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger style={{ minWidth: 170 }}><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          }
        >
          <CardTitle>Uptime stats</CardTitle>
        </CardHeader>
        <CardBody>
          {stats.isLoading ? (
            <TabLoading rows={2} />
          ) : !st ? (
            <EmptyState icon={<Clock size={22} />} title="No data" description="No uptime stats for this period yet." />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Badge tone={st.sla_met ? 'success' : 'danger'} appearance="subtle" size="sm">{st.sla_met ? 'SLA met' : 'SLA breached'}</Badge>
              <StatGroup columns={4}>
                <Stat label="Actual uptime" value={`${st.actual_uptime?.toFixed(2)}%`} dotColor={`var(--vn-${st.sla_met ? 'success' : 'danger'}-solid)`} />
                <Stat label="Target" value={`${st.target_uptime}%`} dotColor="var(--vn-info-solid)" />
                <Stat label="Downtime" value={formatMinutes(st.total_downtime_minutes)} hint={`/ ${formatMinutes(st.allowed_downtime)} allowed`} dotColor="var(--vn-warning-solid)" />
                <Stat label="Incidents" value={String(st.incident_count)} dotColor="var(--vn-accent-solid)" />
              </StatGroup>
            </div>
          )}
        </CardBody>
      </Card>

      <Card flat>
        <CardHeader actions={<Button size="sm" variant="secondary" onClick={openCreate}><Plus size={16} /> Log downtime</Button>}>
          <CardTitle>Downtime logs</CardTitle>
        </CardHeader>
        <CardBody>
          {logs.isLoading ? (
            <TabLoading rows={3} />
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r) => String(r.downtime_log_id)}
              emptyState={<EmptyState icon={<CheckCircle2 size={24} />} title="No downtime" description="No downtime has been logged for this service." />}
            />
          )}
        </CardBody>
      </Card>

      <SlaDowntimeDialog serviceId={id} editing={editing} open={formOpen} onOpenChange={setFormOpen} />
      <SlaEndDowntimeDialog serviceId={id} downtime={ending} open={!!ending} onOpenChange={(o) => !o && setEnding(null)} />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => (o ? undefined : setToDelete(null))}
        title="Delete downtime log"
        description="Delete this log? This action cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
